import { supabase } from '@/utils/supabase';
import { addCalendarDays, pacificMidnightAsUtcMs } from '@/utils/dateHelpers';
import { createComponentLogger } from '@/utils/logger';
import { getRevenueReportData } from '@/services/bankerReconService';
import type {
  BrokeredRevenueRecord,
  CompanyRevenueReportData,
  ServicingCashEvent,
  ServicingPrincipalTieOut
} from '@/types/companyRevenueReport';

const log = createComponentLogger('CompanyRevenueReportService', 'database');

interface PersonRow {
  first_name: string | null;
  last_name: string | null;
}

interface LenderRow {
  investor_name: string | null;
}

interface BrokeredPayoutRow {
  id: string;
  loan_number: string;
  borrower_name: string | null;
  loan_amount: number | string | null;
  finalized_at: string;
  fund_date: string | null;
  gross_check_received: number | string | null;
  lo_reimbursements_total: number | string | null;
  processing_fee: number | string | null;
  company_commission_base: number | string | null;
  e_and_o_deduction: number | string | null;
  lo_commission_base: number | string | null;
  gross_amount: number | string | null;
  net_amount: number | string | null;
  company_total_revenue: number | string | null;
  status: 'approved' | 'paid';
  recipient: PersonRow | null;
  lender: LenderRow | null;
}

interface MentorPayoutRow {
  loan_number: string;
  gross_amount: number | string | null;
  net_amount: number | string | null;
}

interface ServicingLoanJoinRow {
  id: string;
  organization_id: string;
  encompass_loan_id: string;
  loan_number: string;
  borrower_name: string | null;
  investor_name: string | null;
}

interface ServicingPaymentJoinRow {
  id: string;
  servicing_loan_id: string;
  payment_number: number;
  due_date: string;
  payment_received_date: string | null;
  principal_received: number | string | null;
  interest_received: number | string | null;
  escrow_received: number | string | null;
  total_amount_received: number | string | null;
  late_fee: number | string | null;
  payment_method: string | null;
  reference_number: string | null;
  status: string;
  reversed_at: string | null;
  reversal_reason: string | null;
  reversed_payment_received_date: string | null;
  reversed_principal_received: number | string | null;
  reversed_interest_received: number | string | null;
  reversed_escrow_received: number | string | null;
  reversed_total_amount_received: number | string | null;
  reversed_payment_method: string | null;
  reversed_reference_number: string | null;
  loan: ServicingLoanJoinRow;
}

interface LoanOfficerRow {
  encompass_loan_id: string;
  loan_officer_first_name: string | null;
  loan_officer_last_name: string | null;
  loan_officer_2_first_name: string | null;
  loan_officer_2_last_name: string | null;
}

interface PrincipalSettlementRow {
  loan_number: string;
  servicing_principal_collected: number | string | null;
  loan_snapshot: Record<string, unknown> | null;
}

function toNumber(value: unknown): number {
  const parsed = typeof value === 'string' ? Number(value.replace(/,/g, '')) : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

function personName(person: PersonRow | null): string {
  return person ? [person.first_name, person.last_name].filter(Boolean).join(' ') || '—' : '—';
}

function loanOfficerName(row: LoanOfficerRow): string {
  const primary = [row.loan_officer_first_name, row.loan_officer_last_name].filter(Boolean).join(' ');
  const secondary = [row.loan_officer_2_first_name, row.loan_officer_2_last_name].filter(Boolean).join(' ');
  return [primary, secondary].filter(Boolean).join(' / ') || '—';
}

function dateOnlyInRange(value: string, from: string, to: string): boolean {
  return value >= from && value <= to;
}

function timestampBusinessDate(value: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date(value));
}

async function getBrokeredRevenue(
  organizationId: string,
  dateRange: { from: string; to: string }
): Promise<BrokeredRevenueRecord[]> {
  const fromTimestamp = new Date(pacificMidnightAsUtcMs(dateRange.from)).toISOString();
  const toExclusive = new Date(
    pacificMidnightAsUtcMs(addCalendarDays(dateRange.to, 1))
  ).toISOString();

  const { data, error } = await supabase
    .from('payouts')
    .select(`
      id, loan_number, borrower_name, loan_amount, finalized_at, fund_date,
      gross_check_received, lo_reimbursements_total, processing_fee,
      company_commission_base, e_and_o_deduction, lo_commission_base,
      gross_amount, net_amount, company_total_revenue, status,
      recipient:recipient_id(first_name, last_name),
      lender:lender_id(investor_name)
    `)
    .eq('organization_id', organizationId)
    .eq('source_type', 'broker-recon')
    .eq('payout_type', 'brokered')
    .eq('payout_sequence', 1)
    .in('status', ['approved', 'paid'])
    .gte('finalized_at', fromTimestamp)
    .lt('finalized_at', toExclusive)
    .order('finalized_at', { ascending: false });

  if (error) {
    throw error;
  }

  const payouts = (data ?? []) as unknown as BrokeredPayoutRow[];
  const loanNumbers = [...new Set(payouts.map((row) => row.loan_number))];
  const mentorByLoan = new Map<string, { gross: number; net: number }>();

  if (loanNumbers.length > 0) {
    const { data: mentorData, error: mentorError } = await supabase
      .from('payouts')
      .select('loan_number, gross_amount, net_amount')
      .eq('organization_id', organizationId)
      .eq('source_type', 'broker-recon')
      .eq('payout_type', 'mentor')
      .in('status', ['approved', 'paid'])
      .in('loan_number', loanNumbers);

    if (mentorError) {
      throw mentorError;
    }

    for (const mentor of (mentorData ?? []) as MentorPayoutRow[]) {
      const current = mentorByLoan.get(mentor.loan_number) ?? { gross: 0, net: 0 };
      current.gross += toNumber(mentor.gross_amount);
      current.net += toNumber(mentor.net_amount);
      mentorByLoan.set(mentor.loan_number, current);
    }
  }

  return payouts.map((row) => {
    const mentor = mentorByLoan.get(row.loan_number) ?? { gross: 0, net: 0 };
    return {
      id: row.id,
      loanNumber: row.loan_number,
      borrowerName: row.borrower_name || '—',
      loanOfficerName: personName(row.recipient),
      lenderName: row.lender?.investor_name || '—',
      finalizedAt: row.finalized_at,
      fundDate: row.fund_date,
      loanAmount: toNumber(row.loan_amount),
      grossCheckReceived: toNumber(row.gross_check_received),
      loReimbursements: toNumber(row.lo_reimbursements_total),
      processingFee: toNumber(row.processing_fee),
      companyCommissionBase: toNumber(row.company_commission_base),
      eAndO: toNumber(row.e_and_o_deduction),
      loCommissionBase: toNumber(row.lo_commission_base),
      loGrossPayout: toNumber(row.gross_amount),
      loNetPayout: toNumber(row.net_amount),
      mentorGrossPayout: mentor.gross,
      mentorNetPayout: mentor.net,
      companyRevenue: toNumber(row.company_total_revenue),
      status: row.status
    };
  });
}

async function getServicingData(
  organizationId: string,
  dateRange: { from: string; to: string }
): Promise<{ events: ServicingCashEvent[]; tieOuts: ServicingPrincipalTieOut[] }> {
  const { data, error } = await supabase
    .from('servicing_payments')
    .select(`
      id, servicing_loan_id, payment_number, due_date, payment_received_date,
      principal_received, interest_received, escrow_received, total_amount_received,
      late_fee, payment_method, reference_number, status, reversed_at, reversal_reason,
      reversed_payment_received_date, reversed_principal_received,
      reversed_interest_received, reversed_escrow_received,
      reversed_total_amount_received, reversed_payment_method, reversed_reference_number,
      loan:servicing_loans!inner(
        id, organization_id, encompass_loan_id, loan_number, borrower_name, investor_name
      )
    `)
    .eq('loan.organization_id', organizationId)
    .order('due_date', { ascending: true });

  if (error) {
    throw error;
  }

  const payments = (data ?? []) as unknown as ServicingPaymentJoinRow[];
  const encompassIds = [...new Set(payments.map((row) => row.loan.encompass_loan_id).filter(Boolean))];
  const officerByLoan = new Map<string, string>();

  if (encompassIds.length > 0) {
    const { data: officerData, error: officerError } = await supabase
      .from('encompass_loans')
      .select('encompass_loan_id, loan_officer_first_name, loan_officer_last_name, loan_officer_2_first_name, loan_officer_2_last_name')
      .in('encompass_loan_id', encompassIds);

    if (officerError) {
      throw officerError;
    }

    for (const row of (officerData ?? []) as LoanOfficerRow[]) {
      officerByLoan.set(row.encompass_loan_id, loanOfficerName(row));
    }
  }

  const events: ServicingCashEvent[] = [];
  const principalReceivedByLoan = new Map<string, number>();
  const borrowerByLoan = new Map<string, string>();

  for (const payment of payments) {
    const loan = payment.loan;
    borrowerByLoan.set(loan.loan_number, loan.borrower_name || '—');
    const currentPrincipal = toNumber(payment.principal_received);
    principalReceivedByLoan.set(
      loan.loan_number,
      (principalReceivedByLoan.get(loan.loan_number) ?? 0) + currentPrincipal
    );

    const base = {
      paymentId: payment.id,
      dueDate: payment.due_date,
      paymentNumber: payment.payment_number,
      loanNumber: loan.loan_number,
      borrowerName: loan.borrower_name || '—',
      loanOfficerName: officerByLoan.get(loan.encompass_loan_id) || '—',
      investorName: loan.investor_name || '—'
    };

    if (payment.payment_received_date && dateOnlyInRange(payment.payment_received_date, dateRange.from, dateRange.to)) {
      events.push({
        ...base,
        id: `${payment.id}:receipt`,
        eventType: 'receipt',
        activityDate: payment.payment_received_date,
        principal: currentPrincipal,
        interest: toNumber(payment.interest_received),
        escrow: toNumber(payment.escrow_received),
        lateFee: toNumber(payment.late_fee),
        total: toNumber(payment.total_amount_received),
        paymentMethod: payment.payment_method,
        referenceNumber: payment.reference_number,
        status: payment.status,
        reversalReason: null
      });
    }

    if (payment.reversed_at && payment.reversed_payment_received_date) {
      if (dateOnlyInRange(payment.reversed_payment_received_date, dateRange.from, dateRange.to)) {
        events.push({
          ...base,
          id: `${payment.id}:original-receipt`,
          eventType: 'receipt',
          activityDate: payment.reversed_payment_received_date,
          principal: toNumber(payment.reversed_principal_received),
          interest: toNumber(payment.reversed_interest_received),
          escrow: toNumber(payment.reversed_escrow_received),
          lateFee: 0,
          total: toNumber(payment.reversed_total_amount_received),
          paymentMethod: payment.reversed_payment_method,
          referenceNumber: payment.reversed_reference_number,
          status: 'received_then_reversed',
          reversalReason: payment.reversal_reason
        });
      }

      const reversalDate = timestampBusinessDate(payment.reversed_at);
      if (dateOnlyInRange(reversalDate, dateRange.from, dateRange.to)) {
        events.push({
          ...base,
          id: `${payment.id}:reversal`,
          eventType: 'reversal',
          activityDate: reversalDate,
          principal: -toNumber(payment.reversed_principal_received),
          interest: -toNumber(payment.reversed_interest_received),
          escrow: -toNumber(payment.reversed_escrow_received),
          lateFee: 0,
          total: -toNumber(payment.reversed_total_amount_received),
          paymentMethod: payment.reversed_payment_method,
          referenceNumber: payment.reversed_reference_number,
          status: 'reversed',
          reversalReason: payment.reversal_reason
        });
      }
    }
  }

  const { data: settlementData, error: settlementError } = await supabase
    .from('banker_recons')
    .select('loan_number, servicing_principal_collected, loan_snapshot')
    .eq('organization_id', organizationId)
    .in('status', ['finalized', 'pushed'])
    .neq('servicing_principal_collected', 0);

  if (settlementError) {
    throw settlementError;
  }

  const settlementByLoan = new Map<string, number>();
  for (const row of (settlementData ?? []) as PrincipalSettlementRow[]) {
    settlementByLoan.set(
      row.loan_number,
      (settlementByLoan.get(row.loan_number) ?? 0) + toNumber(row.servicing_principal_collected)
    );
    const firstName = typeof row.loan_snapshot?.borrower_first_name === 'string'
      ? row.loan_snapshot.borrower_first_name
      : '';
    const lastName = typeof row.loan_snapshot?.borrower_last_name === 'string'
      ? row.loan_snapshot.borrower_last_name
      : '';
    if (!borrowerByLoan.has(row.loan_number)) {
      borrowerByLoan.set(row.loan_number, [lastName, firstName].filter(Boolean).join(', ') || '—');
    }
  }

  const allLoanNumbers = new Set([...principalReceivedByLoan.keys(), ...settlementByLoan.keys()]);
  const tieOuts = [...allLoanNumbers].map<ServicingPrincipalTieOut>((loanNumber) => {
    const borrowerPrincipalReceived = roundCurrency(principalReceivedByLoan.get(loanNumber) ?? 0);
    const investorPrincipalSettlement = roundCurrency(settlementByLoan.get(loanNumber) ?? 0);
    const variance = roundCurrency(borrowerPrincipalReceived - investorPrincipalSettlement);
    return {
      loanNumber,
      borrowerName: borrowerByLoan.get(loanNumber) || '—',
      borrowerPrincipalReceived,
      investorPrincipalSettlement,
      variance,
      balanced: Math.abs(variance) <= 0.01
    };
  }).sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance) || a.loanNumber.localeCompare(b.loanNumber));

  events.sort((a, b) => b.activityDate.localeCompare(a.activityDate) || a.loanNumber.localeCompare(b.loanNumber));
  return { events, tieOuts };
}

export async function getCompanyRevenueReportData(
  organizationId: string,
  dateRange: { from: string; to: string }
): Promise<CompanyRevenueReportData> {
  const timer = log.startTimer('getCompanyRevenueReportData');
  try {
    const [bankedLoans, brokeredLoans, servicing] = await Promise.all([
      getRevenueReportData(organizationId, dateRange),
      getBrokeredRevenue(organizationId, dateRange),
      getServicingData(organizationId, dateRange)
    ]);

    log.endTimer(timer, {
      metadata: {
        success: true,
        banked: bankedLoans.length,
        brokered: brokeredLoans.length,
        servicingEvents: servicing.events.length
      }
    });
    return {
      bankedLoans,
      brokeredLoans,
      servicingEvents: servicing.events,
      principalTieOuts: servicing.tieOuts
    };
  } catch (error) {
    log.error('Failed to load company revenue report', {
      metadata: { organizationId, dateRange }
    }, error instanceof Error ? error : new Error(String(error)));
    log.endTimer(timer, { metadata: { success: false } });
    throw error;
  }
}
